/**
 * Sistema de permisos configurables por empleado (staff) — FASE 3a (fundación
 * de frontend).
 *
 * ESPEJO del catálogo canónico del backend
 * (`reservamanager-backend/src/shared/permissions.ts`). Las CLAVES deben
 * coincidir EXACTAMENTE con las del backend: son el contrato que viaja en
 * `user.permissions` (desde `/auth/me` y login) y en el body de
 * `PUT /api/users/team/:id/permissions`. El backend es la fuente de verdad de
 * la autorización real; este fichero solo sirve para PINTAR la UI (agrupar,
 * etiquetar, ofrecer plantillas rápidas).
 *
 * Si el backend añade/renombra un permiso, replicar aquí manualmente (no hay
 * generación automática entre los dos repos).
 */

/** Grupos con los que la UI agrupa los permisos. Orden = orden de pintado. */
export const PERMISSION_GROUPS = {
  operativa: "Operativa",
  configuracion: "Configuración",
  datos_sensibles: "Datos sensibles",
  comunicaciones: "Comunicaciones",
  equipo_facturacion: "Equipo y facturación",
} as const;

export type PermissionGroupKey = keyof typeof PERMISSION_GROUPS;

/** Metadatos de cada permiso del catálogo. */
interface PermissionMeta {
  /** Etiqueta en español que muestra el panel de gestión de permisos. */
  label: string;
  /** Grupo al que pertenece (para agrupar en la UI). */
  group: PermissionGroupKey;
}

/**
 * Catálogo canónico (espejo). Las claves son el contrato con el backend.
 *
 * `as const satisfies ...` mantiene las claves como literales (para derivar
 * `PermissionKey`) y a la vez valida que cada entrada tenga la forma correcta.
 */
export const PERMISSIONS = {
  // ---- Operativa ----
  "reservas:gestionar": {
    label:
      "Crear/editar/cambiar estado/cancelar/walk-in/enviar email de reservas",
    group: "operativa",
  },
  "reservas:borrar": {
    label: "Eliminar reservas",
    group: "operativa",
  },
  "citas:gestionar": {
    label: "Gestionar citas y agenda de especialistas",
    group: "operativa",
  },
  "clientes:gestionar": {
    label: "Crear/editar clientes, bloquear, altas/bajas marketing, enviar email",
    group: "operativa",
  },
  "mapa:operar": {
    label: "Bloqueos de mesa, combinaciones y aforos por día",
    group: "operativa",
  },
  "conversaciones:gestionar": {
    label:
      "Conversaciones de WhatsApp (bot IA): responder, tomar el control y cerrar",
    group: "operativa",
  },

  // ---- Configuración ----
  "horario:editar": {
    label: "Horarios, turnos y excepciones",
    group: "configuracion",
  },
  "mesas:editar": {
    label: "Estructura de mesas, salas y objetos",
    group: "configuracion",
  },
  "servicios:editar": {
    label: "Servicios, especialistas y ajustes de citas",
    group: "configuracion",
  },
  "sedes:gestionar": {
    label: "Sedes, logos, portada, visibilidad en app",
    group: "configuracion",
  },
  "perfil_publico:editar": {
    label: "Editar y publicar el perfil público",
    group: "configuracion",
  },

  // ---- Datos sensibles ----
  "clientes:exportar": {
    label: "Exportar/importar/fusionar clientes",
    group: "datos_sensibles",
  },
  "clientes:borrar": {
    label: "Eliminar clientes",
    group: "datos_sensibles",
  },

  // ---- Comunicaciones ----
  "campanas:gestionar": {
    label: "Crear y enviar campañas",
    group: "comunicaciones",
  },
  "integraciones:gestionar": {
    label: "Google Contacts, asistente de voz Carmen y TPV",
    group: "comunicaciones",
  },

  // ---- Equipo y facturación ----
  "equipo:gestionar": {
    label: "Gestionar el equipo y sus permisos",
    group: "equipo_facturacion",
  },
  "facturacion:gestionar": {
    label: "Plan, facturación, add-ons y email del negocio",
    group: "equipo_facturacion",
  },
} as const satisfies Record<string, PermissionMeta>;

/** Unión de las claves canónicas (derivada del catálogo, sin `any`). */
export type PermissionKey = keyof typeof PERMISSIONS;

/** Todas las claves de permiso, en el orden del catálogo. */
export const ALL_PERMISSION_KEYS: PermissionKey[] = Object.keys(
  PERMISSIONS,
) as PermissionKey[];

/** Una plantilla de asignación rápida de permisos para la UI de equipo. */
export interface PermissionPreset {
  id: string;
  label: string;
  keys: PermissionKey[];
}

const OPERATIVA_KEYS: PermissionKey[] = [
  "reservas:gestionar",
  "reservas:borrar",
  "citas:gestionar",
  "clientes:gestionar",
  "mapa:operar",
  "conversaciones:gestionar",
];

/**
 * Plantillas de asignación rápida ofrecidas al configurar los permisos de un
 * empleado (p. ej. botones "Aplicar plantilla" en el modal de equipo). Son
 * solo un atajo de UI: el backend sigue validando/persistiendo el array final
 * de claves, no el id de la plantilla.
 */
export const PERMISSION_PRESETS: PermissionPreset[] = [
  {
    id: "operativa",
    label: "Solo operativa",
    keys: OPERATIVA_KEYS,
  },
  {
    id: "encargado",
    label: "Encargado",
    keys: [
      ...OPERATIVA_KEYS,
      "horario:editar",
      "mesas:editar",
      "servicios:editar",
      "clientes:exportar",
    ],
  },
  {
    id: "todo",
    label: "Acceso total",
    keys: ALL_PERMISSION_KEYS,
  },
];
